import React, { useState } from "react";
import {
  Button,
  Chip,
  Paper,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import BaseDialog from "../../../utils/dialog/BaseDialog";
import { formatAmount } from "../../../../utils/hooks/dataHandler";

const PopulatePriorYaDialog = ({ options, caType, onClose, onPopulate }) => {
  const [selectedKey, setSelectedKey] = useState("");
  const [claimYears, setClaimYears] = useState("");
  const visibleOptions = options.filter((option) => option.caType === caType);
  const selected = visibleOptions.find((option) => option.key === selectedKey);
  const years = Number(selected?.noOfYearClaims ?? claimYears);
  const valid =
    selected &&
    !selected.alreadyAdded &&
    !selected.disabledReason &&
    Number.isInteger(years) &&
    years > 0 &&
    years >= Number(selected.noOfYearsBf);

  return (
    <BaseDialog
      open
      onClose={onClose}
      title='Add from FA Disposal'
      subtitle={`Select a ${caType} row from FA Disposal, then complete any required claim years below the selected row.`}
      icon={<PlaylistAddIcon />}
      maxWidth='sm'
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant='contained'
            disabled={!valid}
            onClick={() => onPopulate(selected.key, years)}
          >
            Add row
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        {visibleOptions.length === 0 ? (
          <Paper
            variant='outlined'
            sx={{ p: 3, borderRadius: 2, bgcolor: "action.hover" }}
          >
            <Stack alignItems='center' spacing={1} sx={{ textAlign: "center" }}>
              <PlaylistAddRoundedIcon
                sx={{ fontSize: 32, color: "text.secondary" }}
              />
              <Typography variant='body2' fontWeight={600}>
                No {caType} disposal records available
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Save FA Disposal and reopen this schedule to refresh.
              </Typography>
            </Stack>
          </Paper>
        ) : (
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox' />
                  <TableCell>YA of purchase</TableCell>
                  <TableCell>CA Name</TableCell>
                  <TableCell align='center'>No. of years b/f</TableCell>
                  <TableCell align='right'>Disposal TWDV</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleOptions.map((option) => {
                  const select = () => {
                    if (
                      option.alreadyAdded ||
                      option.disabledReason ||
                      selectedKey === option.key
                    )
                      return;
                    setSelectedKey(option.key);
                    setClaimYears("");
                  };
                  return (
                    <React.Fragment key={option.key}>
                      <TableRow
                        hover={!option.alreadyAdded && !option.disabledReason}
                        selected={selectedKey === option.key}
                        onClick={select}
                      >
                        <TableCell padding='checkbox'>
                          <Radio
                            checked={selectedKey === option.key}
                            disabled={
                              option.alreadyAdded || !!option.disabledReason
                            }
                            onChange={select}
                            inputProps={{
                              "aria-label": `Select ${option.caName}, YA ${option.yaOfPurchase}, ${option.noOfYearsBf} years b/f`,
                            }}
                          />
                        </TableCell>
                        <TableCell>{option.yaOfPurchase}</TableCell>
                        <TableCell>{option.caName}</TableCell>
                        <TableCell align='center'>
                          {option.noOfYearsBf}
                        </TableCell>
                        <TableCell align='right'>
                          {formatAmount(option.twdv)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size='small'
                            variant='outlined'
                            label={
                              option.disabledReason ||
                              (option.alreadyAdded
                                ? "Already added"
                                : "Available")
                            }
                          />
                        </TableCell>
                      </TableRow>
                      {selectedKey === option.key &&
                        option.noOfYearClaims == null && (
                          <TableRow sx={{ bgcolor: "action.selected" }}>
                            <TableCell colSpan={6} sx={{ py: 2 }}>
                              <Stack spacing={1.5}>
                                <Typography variant='subtitle2'>
                                  Enter the number of years of claim to continue
                                </Typography>
                                <TextField
                                  autoFocus
                                  fullWidth
                                  label='No. of years of claim'
                                  value={claimYears}
                                  onChange={(event) =>
                                    setClaimYears(event.target.value)
                                  }
                                  type='number'
                                  required
                                  size='small'
                                  error={claimYears !== "" && !valid}
                                  inputProps={{
                                    min: Math.max(
                                      1,
                                      Number(option.noOfYearsBf) || 0
                                    ),
                                    step: 1,
                                  }}
                                  helperText={`Enter a whole number of at least ${Math.max(1, Number(option.noOfYearsBf) || 0)}. FA Disposal provides years b/f, not the original claim period.`}
                                />
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </BaseDialog>
  );
};

export default PopulatePriorYaDialog;
